import React, { useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../../components/common/ToastMsg';
import { ENGAGE_ENQUIRY_CONVERT_URL } from '../../constants/api';

const LANGUAGES = [
  ['en', 'English'], ['ta', 'Tamil'], ['hi', 'Hindi'],
  ['te', 'Telugu'], ['ml', 'Malayalam'], ['kn', 'Kannada'],
];

const inputCls = 'w-full px-2.5 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500';

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

// Turns an enquiry into a Patient record. Mobile is always taken from the
// enquiry; everything else is prefilled where the enquiry already knows it.
export default function ConvertToPatientModal({ enquiry, onClose, onConverted }) {
  const { create: apiCreate } = useApiMethods();
  const [first, ...rest] = (enquiry.name || '').trim().split(' ');
  const [form, setForm] = useState({
    first_name: first || '', last_name: rest.join(' '),
    gender: '', date_of_birth: '', uhid: '',
    email: enquiry.email || '', address: '', city: '', state: '', pincode: '',
    preferred_language: LANGUAGES.some(([v]) => v === enquiry.preferred_language) ? enquiry.preferred_language : 'en',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.first_name.trim() || saving) return;
    setSaving(true);
    try {
      const payload = { ...form, date_of_birth: form.date_of_birth || null };
      // apiWrapper returns failures (status 'fail' + the body) instead of throwing.
      const resp = await apiCreate(ENGAGE_ENQUIRY_CONVERT_URL(enquiry.id), payload);
      if (resp?.status !== 'success') {
        const fieldErr = Object.entries(resp || {})
          .filter(([k]) => !['status', 'message'].includes(k))
          .map(([k, v]) => `${k}: ${[].concat(v).join(' ')}`)[0];
        ToastMsg('error', fieldErr || resp?.message || 'Conversion failed');
        return;
      }
      ToastMsg('success', `${enquiry.name} converted to patient`);
      onConverted?.(resp.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">Convert to Patient</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Creates a patient record for {enquiry.name} ({enquiry.mobile}) and moves the enquiry to its Converted stage.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="First name *">
            <input value={form.first_name} onChange={set('first_name')} className={inputCls} />
          </Field>
          <Field label="Last name">
            <input value={form.last_name} onChange={set('last_name')} className={inputCls} />
          </Field>
          <Field label="Gender">
            <select value={form.gender} onChange={set('gender')} className={inputCls}>
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Date of birth">
            <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} className={inputCls} />
          </Field>
          <Field label="UHID">
            <input value={form.uhid} onChange={set('uhid')} placeholder="Hospital patient ID, if known" className={inputCls} />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={set('email')} className={inputCls} />
          </Field>
          <Field label="Address" className="sm:col-span-2">
            <input value={form.address} onChange={set('address')} className={inputCls} />
          </Field>
          <Field label="City">
            <input value={form.city} onChange={set('city')} className={inputCls} />
          </Field>
          <Field label="State">
            <input value={form.state} onChange={set('state')} className={inputCls} />
          </Field>
          <Field label="Pincode">
            <input value={form.pincode} onChange={set('pincode')} className={inputCls} />
          </Field>
          <Field label="Preferred language">
            <select value={form.preferred_language} onChange={set('preferred_language')} className={inputCls}>
              {LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !form.first_name.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm shadow-sm hover:bg-green-700 disabled:opacity-50">
            <CheckCircle className="w-3.5 h-3.5" /> {saving ? 'Converting…' : 'Convert'}
          </button>
        </div>
      </div>
    </div>
  );
}
